import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { track } from '../../utils/analytics';

import { RootState } from '../../store/reducers';
import { ScheduleListItemData } from '../../typing/scheduleItem';
import { ModalConfig } from '../global/ConfirmModal';
import { Report, ReportSummary } from '../../utils/classes';
import { getDistance } from '../../utils/geolocation';
import getDistanceTreshold from '../submitreport/utils/getDistanceTreshold';
import { currentPeriodEndDate } from '../../utils/schedule';
import { reportActions } from '../../store/reducers/report';
import { CurrentLocation, geolocationActions, getCurrentLocation } from '../../store/reducers/geolocation';
import * as reportcacheThunks from '../../store/reducers/reportcache/reportcache.actionThunk';
import { scheduleSitesThunk } from '../../store/reducers/schedulesites';
import { siteActions } from '../../store/reducers/site/site';
import RootNavigation from '../../RootNavigation';
import { errorLogger } from '../../utils/errorLogger';
import { toastActions } from '../../store/reducers/toast';

import { selectProfile } from '../../store/selectors/profile';
const DEFAULT_MODAL_CONFIG: ModalConfig = {
  title: '',
  subtitle: '',
  options: []
};

type GeoPrecision = 'low' | 'medium' | 'high' | 'none';

export default function useHomeCheckIn() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['site', 'common', 'home']);

  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalConfig, setModalConfig] = useState<ModalConfig>(DEFAULT_MODAL_CONFIG);

  // Redux state
  const auth = useSelector((state: RootState) => state.firebase.auth);
  const profile = useSelector((state: RootState) => selectProfile(state));
  const cacheLocation = useSelector((state: RootState) => state.geolocation.cacheLocation);
  const organization = useSelector((state: RootState) => state.organization.myOrganization);
  const isConnected = useSelector((state: RootState) => state.network.isConnected);
  const profileColor = useSelector((state: RootState) => state.profiletheme.color);
  const cacheReports = useSelector((state: RootState) => state.reportcache.reports);
  const cacheSummaries = useSelector((state: RootState) => state.reportcache.summaries);
  const pendingSites = useSelector((state: RootState) => state.reportsubmit.pendingSites);
  const scheduledSites = useSelector<RootState, any[]>(state => state.scheduleSites.myScheduledSites);
  const selectedSite = useSelector((state: RootState) => state.site.selectedSite);
  const selectedSiteKey = useSelector((state: RootState) => state.site.selectedSiteKey);
  const selectedSiteSchedule = useSelector((state: RootState) => state.site.selectedSiteSchedule);
  const selectedSiteCompletedCount = useSelector((state: RootState) => state.site.selectedSiteCompletedCount);
  const selectedSiteLastReport = useSelector((state: RootState) => state.site.selectedSiteLastReport);

  const getEffectiveRadius = useCallback(
    (item: ScheduleListItemData): GeoPrecision => {
      const orgGeoPrecision = organization?.geoPrecision;
      const siteRadius = item.siteRadius;
      const effectiveRadius = siteRadius && siteRadius !== 'default' ? siteRadius : orgGeoPrecision;

      // Follow the legacy site flow: if org/site precision is temporarily unavailable,
      // do not silently disable GPS. Fall back to the Organization class default.
      return (effectiveRadius || 'high') as GeoPrecision;
    },
    [organization]
  );

  const resolveCheckInLocation = useCallback(
    async (item: ScheduleListItemData): Promise<CurrentLocation | null> => {
      const effectiveRadius = getEffectiveRadius(item);
      const gpsRequired = effectiveRadius !== 'none';

      if (!gpsRequired) {
        return cacheLocation?.isFetched ? cacheLocation : null;
      }

      try {
        const latestLocation = await getCurrentLocation();
        dispatch(geolocationActions.setCacheLocation(latestLocation));
        return latestLocation;
      } catch (error: any) {
        if (cacheLocation?.isFetched) {
          return cacheLocation;
        }

        const isPermissionDenied = error?.code === 1;
        const errorMessage = isPermissionDenied
          ? `${t('site:error.failedRetrieveLocation')}\n${t('site:error.enableLocationAccess')}`
          : t('site:error.failedRetrieveLocation');

        throw new Error(errorMessage);
      }
    },
    [cacheLocation, dispatch, getEffectiveRadius, t]
  );

  // ---------------------------------------------------------------------------
  // GPS validation
  // ---------------------------------------------------------------------------
  const validateGps = useCallback(
    (item: ScheduleListItemData, currentLocation: CurrentLocation | null): boolean => {
      const effectiveRadius = getEffectiveRadius(item);

      if (effectiveRadius === 'none') return true;

      if (!item.siteCoordinates) return true; // no site coords → skip

      const distance = currentLocation?.isFetched ? getDistance(currentLocation, item.siteCoordinates) : 9999;
      const threshold = getDistanceTreshold(effectiveRadius);

      if (distance === null || distance >= threshold) {
        setModalConfig({
          title: t('site:checkIn'),
          subtitle: t('site:outlet.error.farFromLocation'),
          options: [
            {
              text: t('site:ok'),
              style: 'confirm',
              action: () => {
                setShowConfirmModal(false);
                setIsCheckingIn(false);
              }
            }
          ]
        });
        setShowConfirmModal(true);
        return false;
      }
      return true;
    },
    [getEffectiveRadius, t]
  );

  // ---------------------------------------------------------------------------
  // Create Report object
  // ---------------------------------------------------------------------------
  const buildReport = useCallback(
    (item: ScheduleListItemData, isAdhoc: boolean) => {
      const now = moment().toISOString(true);
      const date = item.scheduledDate;

      const periodEnd = organization?.schedule ? currentPeriodEndDate(organization.schedule) : null;

      let scheduledAt = item.siteTimezone ? moment.tz(date, item.siteTimezone) : moment(date);

      if (item.hasStrictTime && item.startTime > 0) {
        scheduledAt = scheduledAt.startOf('day').add(item.startTime, 'minutes');
      }

      // Questions are intentionally empty — server populates them via hydrateLiteReport
      // after check-in (aligns with audit-lite server-side assembly pattern).
      const report = new Report(
        [],
        [],
        'draft',
        item.siteID,
        auth.uid,
        [],
        periodEnd ? periodEnd.toISOString(true) : '',
        now,
        '',
        '',
        scheduledAt.toISOString(true),
        false,
        '',
        '',
        [],
        (item.selfieSignatures as any) ?? [],
        isAdhoc,
        false
      );

      report.scheduleKey = item.scheduleID;
      report.hasDeadline = item.hasStrictTime ?? false;
      report.startTime = item.startTime;
      report.endTime = item.endTime;

      const summary: ReportSummary = {
        auditor: auth.uid,
        datetimeIn: now,
        datetimeOut: '',
        datetimeUpdated: now,
        status: 'draft',
        questionnaire: '',
        isMakeUp: false,
        datetimeScheduled: scheduledAt.toISOString(true),
        isAdhoc,
        scheduleKey: item.scheduleID
      };

      const reportKey = `${date}_${moment(now).valueOf()}`;

      return { report, summary, reportKey };
    },
    [auth, organization]
  );

  const syncSelectedSite = useCallback(
    (item: ScheduleListItemData, reportLike?: { questions?: any[] }) => {
      const matchingScheduledSite = scheduledSites.find((site: any) => site.siteKey === item.siteID);
      const baseSite =
        selectedSite && selectedSiteKey === item.siteID ? selectedSite : matchingScheduledSite?.siteProfile;

      if (!baseSite) {
        return;
      }

      dispatch(
        siteActions.selectSite(
          item.siteID,
          {
            ...baseSite,
            emailTargets: item.emailTargets ?? baseSite.emailTargets ?? [],
            signatures: item.signatures ?? baseSite.signatures ?? 0
          } as any,
          selectedSiteKey === item.siteID ? selectedSiteSchedule : null,
          selectedSiteKey === item.siteID ? selectedSiteCompletedCount : matchingScheduledSite?.completedCount ?? null,
          selectedSiteKey === item.siteID ? selectedSiteLastReport : null
        )
      );
    },
    [
      dispatch,
      scheduledSites,
      selectedSite,
      selectedSiteCompletedCount,
      selectedSiteKey,
      selectedSiteLastReport,
      selectedSiteSchedule
    ]
  );

  // ---------------------------------------------------------------------------
  // Main check-in handler
  // ---------------------------------------------------------------------------
  const handleCheckIn = useCallback(
    async (item: ScheduleListItemData, isAdhoc: boolean, closeModal: () => void) => {
      setIsCheckingIn(true);

      try {
        // 1. Validate network
        if (!isConnected) {
          setModalConfig({
            title: t('common:error'),
            subtitle: t('site:outlet.offlineInfo'),
            options: [
              {
                text: t('site:ok'),
                style: 'confirm',
                action: () => {
                  setShowConfirmModal(false);
                  setIsCheckingIn(false);
                }
              }
            ]
          });
          setShowConfirmModal(true);
          return;
        }

        // 2. Resolve latest location and validate GPS distance
        let resolvedLocation: CurrentLocation | null;
        try {
          resolvedLocation = await resolveCheckInLocation(item);
        } catch (locationError: any) {
          closeModal();
          setModalConfig({
            title: t('site:error.locationErrorTitle'),
            subtitle: locationError?.message || t('site:error.failedRetrieveLocation'),
            options: [
              {
                text: t('site:ok'),
                style: 'confirm',
                action: () => {
                  setShowConfirmModal(false);
                  setIsCheckingIn(false);
                }
              }
            ]
          });
          setShowConfirmModal(true);
          setIsCheckingIn(false);
          return;
        }

        if (!validateGps(item, resolvedLocation)) {
          closeModal();
          setIsCheckingIn(false);
          return;
        }

        // 3. Build report
        const { report, summary, reportKey } = buildReport(item, isAdhoc);

        // 4. Call check-in API (createReport thunk handles both Lite API and Firebase fallback)
        const effectiveReportKey = await dispatch(
          reportcacheThunks.createReport(item.siteID, reportKey, report, summary, {
            scheduleDate: item.scheduledDate,
            scheduleType: item.scheduleType,
            gpsPrecision: getEffectiveRadius(item),
            coordinates: resolvedLocation?.isFetched
              ? {
                  latitude: resolvedLocation.coords.latitude,
                  longitude: resolvedLocation.coords.longitude
                }
              : null
          }) as any
        );

        let selectedReport = report;

        // Always hydrate from server for single-site — aligns with audit-lite pattern.
        // hydrateLiteReport re-fetches on entry and applies server-newer-wins, so a draft
        // edited on another device is picked up here (with offline/error fallback to cache).
        if (!item.isMultiSite) {
          selectedReport = await dispatch(
            reportcacheThunks.hydrateLiteReport(item.siteID, effectiveReportKey, report, summary) as any
          );
        }

        if (!item.isMultiSite && (!selectedReport.questions || selectedReport.questions.length === 0)) {
          throw new Error(t('site:outlet.error.failedCompilingData'));
        }

        syncSelectedSite(item, selectedReport);

        track('check_in_success', { scheduleId: item.scheduleID, siteId: item.siteID, isAdhoc });

        // 5. Select report in Redux
        dispatch(reportActions.selectReport(effectiveReportKey, selectedReport));

        if (organization?.schedule) {
          dispatch(scheduleSitesThunk.getMyScheduledSites(organization.schedule, item.scheduledDate) as any);
        }

        // 6. Close modals
        closeModal();

        // 7. Navigate
        if (item.isMultiSite) {
          RootNavigation.navigate('Lobby', {
            profileColor,
            originTab: 'HomeTab',
            siteName: item.siteName
          });
        } else {
          RootNavigation.navigate('Questionnaire', {
            originTab: 'HomeTab',
            siteName: item.siteName,
            profileColor
          });
        }
      } catch (error: any) {
        errorLogger(auth.uid, profile?.organization, item.siteID, '', error, 'useHomeCheckIn/handleCheckIn');
        track('check_in_failure', {
          scheduleId: item.scheduleID,
          siteId: item.siteID,
          error: error?.message || 'unknown'
        });

        const isFutureDateError = error?.message?.toLowerCase().includes('future date');
        if (isFutureDateError) closeModal();
        setModalConfig({
          title: isFutureDateError ? t('site:checkIn') : t('common:error'),
          subtitle: error?.message || t('site:outlet.error.failedCompilingData'),
          options: [
            {
              text: t('site:ok'),
              style: 'confirm',
              action: () => {
                setShowConfirmModal(false);
                setIsCheckingIn(false);
              }
            }
          ]
        });
        setShowConfirmModal(true);
      } finally {
        setIsCheckingIn(false);
      }
    },
    [
      isConnected,
      resolveCheckInLocation,
      validateGps,
      buildReport,
      dispatch,
      getEffectiveRadius,
      organization,
      profileColor,
      syncSelectedSite,
      auth,
      profile,
      t
    ]
  );

  // ---------------------------------------------------------------------------
  // Continue draft handler
  // ---------------------------------------------------------------------------
  const handleContinueDraft = useCallback(
    async (item: ScheduleListItemData, closeModal: () => void) => {
      const siteReports = cacheReports[item.siteID];
      const siteSummaries = cacheSummaries[item.siteID];

      // Find draft report matching this schedule in local cache
      const draftKey = siteSummaries
        ? Object.keys(siteSummaries).find(key => {
            const sum = siteSummaries[key];
            return sum?.scheduleKey === item.scheduleID && sum?.status === 'draft';
          })
        : undefined;

      const hasLocalDraft = Boolean(draftKey && siteReports?.[draftKey]);

      // Multi-site continues directly from the local section cache (section-based flow,
      // no single-report hydrate). Single-site always re-fetches below.
      if (item.isMultiSite && hasLocalDraft) {
        const draftReport = siteReports![draftKey!];
        syncSelectedSite(item, draftReport as any);
        dispatch(reportActions.selectReport(draftKey!, draftReport));
        closeModal();
        RootNavigation.navigate('Lobby', { profileColor, originTab: 'HomeTab', siteName: item.siteName });
        return;
      }

      // Re-fetch on every entry so a draft edited on another device is picked up.
      // hydrateLiteReport is server-newer-wins with offline/error fallback to the local
      // cache, so an unsynced local draft is preserved when the server copy is not newer.
      const effectiveKey = hasLocalDraft ? draftKey! : item.firebaseReportID?.trim();
      if (!effectiveKey) {
        dispatch(toastActions.createToast(t('site:error.missingDraftReport'), true, 3000));
        return;
      }

      setIsCheckingIn(true);
      try {
        const { report: fallbackReport, summary: fallbackSummary } = buildReport(item, item.scheduleType === 'adhoc');
        const hydratedReport = await dispatch(
          reportcacheThunks.hydrateLiteReport(item.siteID, effectiveKey, fallbackReport, fallbackSummary) as any
        );

        syncSelectedSite(item, hydratedReport);
        dispatch(reportActions.selectReport(effectiveKey, hydratedReport));
        closeModal();
        if (item.isMultiSite) {
          RootNavigation.navigate('Lobby', { profileColor, originTab: 'HomeTab', siteName: item.siteName });
        } else {
          RootNavigation.navigate('Questionnaire', { originTab: 'HomeTab', siteName: item.siteName, profileColor });
        }
      } catch (error: any) {
        dispatch(toastActions.createToast(error?.message || t('site:error.missingDraftReport'), true, 3000));
      } finally {
        setIsCheckingIn(false);
      }
    },
    [buildReport, cacheReports, cacheSummaries, dispatch, profileColor, setIsCheckingIn, syncSelectedSite, t]
  );

  // ---------------------------------------------------------------------------
  // Reset (delete) draft handler
  // ---------------------------------------------------------------------------
  const handleResetDraft = useCallback(
    async (item: ScheduleListItemData, closeModal: () => void) => {
      setIsCheckingIn(true);
      try {
        const siteSummaries = cacheSummaries[item.siteID];
        const draftKey = siteSummaries
          ? Object.keys(siteSummaries).find(key => {
              const sum = siteSummaries[key];
              return sum?.scheduleKey === item.scheduleID && sum?.status === 'draft';
            })
          : undefined;

        const fallbackDraftKey = item.firebaseReportID?.trim() || undefined;
        const targetDraftKey = draftKey || fallbackDraftKey;

        if (!targetDraftKey) {
          dispatch(toastActions.createToast(t('site:error.missingDraftReport'), true, 3000));
          return;
        }

        await dispatch(reportcacheThunks.removeDraftReport(item.siteID, targetDraftKey) as any);

        closeModal();

        if (organization?.schedule) {
          dispatch(
            scheduleSitesThunk.getMyScheduledSites(
              organization.schedule,
              item.scheduledDate ? moment(item.scheduledDate).format('YYYY-MM-DD') : undefined
            ) as any
          );
        }
      } catch (error: any) {
        setModalConfig({
          title: t('common:error'),
          subtitle: error?.message || t('site:outlet.error.failedCompilingData'),
          options: [
            {
              text: t('site:ok'),
              style: 'confirm',
              action: () => {
                setShowConfirmModal(false);
                setIsCheckingIn(false);
              }
            }
          ]
        });
        setShowConfirmModal(true);
      } finally {
        setIsCheckingIn(false);
      }
    },
    [cacheSummaries, dispatch, organization, t]
  );

  // ---------------------------------------------------------------------------
  // Sync with Server — load pending report into Redux then go to SubmitReport
  // ---------------------------------------------------------------------------
  const handleSyncWithServer = useCallback(
    (item: ScheduleListItemData, closeModal: () => void) => {
      console.log('pendingSites', pendingSites, item.siteID, item.scheduleID);
      const reportKey = pendingSites?.[item.siteID]?.pendingSingle?.[item.scheduleID];
      const report = reportKey ? cacheReports?.[item.siteID]?.[reportKey] : null;

      if (!reportKey || !report) {
        dispatch(toastActions.createToast(t('site:error.missingDraftReport'), true, 3000));
        return;
      }

      syncSelectedSite(item, report as any);
      dispatch(reportActions.selectReport(reportKey, report));
      closeModal();

      RootNavigation.navigate('SubmitReport', {
        profileColor,
        originTab: 'HomeTab',
        siteName: item.siteName
      });
    },
    [cacheReports, dispatch, pendingSites, profileColor, syncSelectedSite, t]
  );

  // ---------------------------------------------------------------------------
  // Modify pending upload — load the cached report into Redux then navigate
  // ---------------------------------------------------------------------------
  const handleModifyPendingUpload = useCallback(
    (item: ScheduleListItemData, closeModal: () => void) => {
      const reportKey = pendingSites?.[item.siteID]?.pendingSingle?.[item.scheduleID];
      const report = reportKey ? cacheReports?.[item.siteID]?.[reportKey] : null;

      if (!reportKey || !report) {
        dispatch(toastActions.createToast(t('site:error.missingDraftReport'), true, 3000));
        return;
      }

      syncSelectedSite(item, report as any);
      dispatch(reportActions.selectReport(reportKey, report));
      closeModal();

      if (item.isMultiSite) {
        RootNavigation.navigate('Lobby', {
          profileColor,
          originTab: 'HomeTab',
          siteName: item.siteName
        });
      } else {
        RootNavigation.navigate('Questionnaire', {
          originTab: 'HomeTab',
          siteName: item.siteName,
          profileColor
        });
      }
    },
    [cacheReports, dispatch, pendingSites, profileColor, syncSelectedSite, t]
  );

  // ---------------------------------------------------------------------------
  // Show check-in confirmation modal
  // ---------------------------------------------------------------------------
  const showCheckInConfirmation = useCallback(
    (item: ScheduleListItemData, isAdhoc: boolean, closeCheckInModal: () => void) => {
      setModalConfig({
        title: t('site:checkIn'),
        subtitle: t('site:confirmCheckIn'),
        options: [
          {
            text: t('common:yes'),
            action: () => {
              setShowConfirmModal(false);
              handleCheckIn(item, isAdhoc, closeCheckInModal);
            },
            style: 'confirm'
          },
          {
            text: t('common:cancel'),
            action: () => {
              setShowConfirmModal(false);
              setIsCheckingIn(false);
            },
            style: 'cancel'
          }
        ]
      });
      setShowConfirmModal(true);
    },
    [handleCheckIn, t]
  );

  return {
    isCheckingIn,
    showConfirmModal,
    modalConfig,
    setShowConfirmModal,
    showCheckInConfirmation,
    handleCheckIn,
    handleContinueDraft,
    handleResetDraft,
    handleSyncWithServer,
    handleModifyPendingUpload
  };
}
